import { appConfig } from "../../shared/brand.js";
import { playRealtimeUpdateSound, shouldSuppressRealtimeSound } from "./audio";

const reconnectDelaysMs = [1000, 2000, 5000];
const liveSnapshotType = "live_snapshot";
const allowedTopics = new Set(["home", "market", "leaderboards", "notable-events", "comments"]);
const liveHealthPath = "/api/live/health";

let clientId = "";
const unavailableLiveOrigins = new Set();
const liveOriginHealthChecks = new Map();

export function subscribeLive({
  topics = [],
  marketId = "",
  marketIds = [],
  onMarketUpdate,
  onLeaderboardsUpdate,
  onNotableEventsUpdate,
  onCommentsUpdate,
  onStatusChange
} = {}) {
  if (!isLiveRealtimeEnabled()) {
    emitLiveDebug("disabled");
    safeCall(onStatusChange, "INIT_DISABLED");
    return () => {};
  }

  const topicList = normalizeTopics(topics);
  if (!topicList.length) {
    safeCall(onStatusChange, "INIT_NO_TOPICS");
    return () => {};
  }
  const origin = liveOrigin();
  if (!origin) {
    safeCall(onStatusChange, "INIT_MISSING_ORIGIN");
    return () => {};
  }
  try {
    websocketOrigin(origin);
  } catch {
    safeCall(onStatusChange, "INIT_INVALID_ORIGIN");
    return () => {};
  }
  if (unavailableLiveOrigins.has(origin)) {
    safeCall(onStatusChange, "INIT_UNAVAILABLE");
    return () => {};
  }

  let socket = null;
  let reconnectTimer = null;
  let reconnectAttempt = 0;
  let active = true;
  let failed = false;
  const voteTotalsByMarket = new Map();

  const connect = async () => {
    if (!active || failed) return;
    safeCall(onStatusChange, "CHECKING");
    if (!(await ensureLiveOriginAvailable(origin))) {
      safeCall(onStatusChange, "UNAVAILABLE");
      scheduleReconnect();
      return;
    }

    const url = buildLiveSocketUrl({
      origin,
      topics: topicList,
      marketId,
      marketIds
    });
    safeCall(onStatusChange, "CONNECTING");
    try {
      socket = new WebSocket(url);
    } catch {
      safeCall(onStatusChange, "ERROR");
      scheduleReconnect();
      return;
    }

    socket.addEventListener("open", () => {
      reconnectAttempt = 0;
      safeCall(onStatusChange, "OPEN");
    });
    socket.addEventListener("message", (event) => {
      handleLiveMessage({
        data: event.data,
        voteTotalsByMarket,
        onMarketUpdate,
        onLeaderboardsUpdate,
        onNotableEventsUpdate,
        onCommentsUpdate
      });
    });
    socket.addEventListener("close", () => {
      safeCall(onStatusChange, "CLOSED");
      scheduleReconnect();
    });
    socket.addEventListener("error", () => {
      safeCall(onStatusChange, "ERROR");
      try {
        socket?.close();
      } catch {
        scheduleReconnect();
      }
    });
  };

  const scheduleReconnect = () => {
    if (!active || reconnectTimer || failed) return;
    if (reconnectAttempt >= reconnectDelaysMs.length) {
      markLiveRealtimeFailed();
      return;
    }
    const delay = reconnectDelaysMs[reconnectAttempt];
    reconnectAttempt += 1;
    reconnectTimer = globalThis.setTimeout(() => {
      reconnectTimer = null;
      startConnect();
    }, delay);
  };

  const markLiveRealtimeFailed = () => {
    if (failed) return;
    failed = true;
    unavailableLiveOrigins.add(origin);
    safeCall(onStatusChange, "FAILED");
  };

  const startConnect = () => {
    connect().catch(() => {
      markLiveRealtimeFailed();
    });
  };

  startConnect();

  return () => {
    active = false;
    if (reconnectTimer) {
      globalThis.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket && socket.readyState <= WebSocket.OPEN) {
      socket.close();
    }
    socket = null;
  };
}

export function buildLiveSocketUrl({ origin, topics, marketId = "", marketIds = [] }) {
  const url = new URL("/live/totals", websocketOrigin(origin));
  url.searchParams.set("topics", normalizeTopics(topics).join(","));
  const safeMarketId = String(marketId || "").trim();
  if (safeMarketId) url.searchParams.set("marketId", safeMarketId);
  const safeMarketIds = normalizeList(marketIds);
  if (safeMarketIds.length) url.searchParams.set("marketIds", safeMarketIds.join(","));
  url.searchParams.set("clientId", getClientId());
  return url.toString();
}

export function isValidLiveSnapshot(payload) {
  return (
    payload?.type === liveSnapshotType &&
    payload.version === 1 &&
    Array.isArray(payload.topics) &&
    payload.topics.every((topic) => allowedTopics.has(topic))
  );
}

function handleLiveMessage({
  data,
  voteTotalsByMarket,
  onMarketUpdate,
  onLeaderboardsUpdate,
  onNotableEventsUpdate,
  onCommentsUpdate
}) {
  let payload;
  try {
    payload = JSON.parse(data);
  } catch {
    return;
  }
  if (!isValidLiveSnapshot(payload)) return;

  if (Array.isArray(payload.markets) && payload.markets.length) {
    const changedMarketId = findChangedVoteMarketId(payload.markets, voteTotalsByMarket);
    if (changedMarketId && !shouldSuppressRealtimeSound(changedMarketId)) {
      playRealtimeUpdateSound();
    }
    safeCallOnce(onMarketUpdate, payload);
  }
  if (payload.leaderboards && payload.topics.includes("leaderboards")) {
    safeCall(onLeaderboardsUpdate, payload);
  }
  if (Array.isArray(payload.notableEvents) && payload.topics.includes("notable-events")) {
    safeCall(onNotableEventsUpdate, payload);
  }
  if (Array.isArray(payload.comments) && payload.comments.length) {
    safeCall(onCommentsUpdate, payload);
  }
}

function findChangedVoteMarketId(markets, voteTotalsByMarket) {
  let changedMarketId = "";

  for (const market of markets) {
    const marketId = String(market?.marketId || market?.id || "");
    if (!marketId) continue;

    const nextTotals = collectOptionVoteTotals(market);
    const previousTotals = voteTotalsByMarket.get(marketId);
    voteTotalsByMarket.set(marketId, nextTotals);

    if (!previousTotals || changedMarketId) continue;
    for (const [optionId, totalVotes] of nextTotals.entries()) {
      if (previousTotals.has(optionId) && previousTotals.get(optionId) !== totalVotes) {
        changedMarketId = marketId;
        break;
      }
    }
  }

  return changedMarketId;
}

function collectOptionVoteTotals(market) {
  const totals = new Map();
  for (const option of Array.isArray(market?.options) ? market.options : []) {
    const optionId = String(option?.id || option?.optionId || "");
    if (!optionId) continue;
    const totalVotes = Number(option?.totalVotes ?? option?.votes ?? 0);
    totals.set(optionId, Number.isFinite(totalVotes) ? totalVotes : 0);
  }
  return totals;
}

function isLiveRealtimeEnabled() {
  return import.meta.env.VITE_ENABLE_LIVE_REALTIME === "1" && import.meta.env.VITE_DISABLE_API !== "1";
}

function liveOrigin() {
  const configured = String(import.meta.env.VITE_LIVE_ORIGIN || "").trim();
  if (!import.meta.env.PROD && configured) return configured;
  return import.meta.env.PROD ? appConfig.liveOrigin : "";
}

function websocketOrigin(origin) {
  const normalized = String(origin || "").trim();
  if (!normalized) throw new Error("VITE_LIVE_ORIGIN is required for live realtime.");
  if (normalized.startsWith("https://")) return normalized.replace(/^https:\/\//, "wss://");
  if (normalized.startsWith("http://")) return normalized.replace(/^http:\/\//, "ws://");
  if (normalized.startsWith("wss://") || normalized.startsWith("ws://")) return normalized;
  throw new Error("VITE_LIVE_ORIGIN must be an http(s) or ws(s) origin.");
}

async function ensureLiveOriginAvailable(origin) {
  if (!shouldCheckLiveHealth()) return true;
  if (unavailableLiveOrigins.has(origin)) return false;

  let check = liveOriginHealthChecks.get(origin);
  if (!check) {
    check = fetchLiveHealth()
      .then((available) => available)
      .catch(() => false)
      .finally(() => {
        liveOriginHealthChecks.delete(origin);
      });
    liveOriginHealthChecks.set(origin, check);
  }

  return check;
}

function shouldCheckLiveHealth() {
  return import.meta.env.MODE === "production" && import.meta.env.VITE_DISABLE_LIVE_HEALTH_CHECK !== "1";
}

async function fetchLiveHealth() {
  if (typeof fetch !== "function") return true;
  const response = await fetch(liveHealthPath, {
    method: "GET",
    headers: { accept: "application/json" },
    cache: "no-store",
    credentials: "same-origin"
  });
  if (!response.ok) return false;
  const payload = await response.json().catch(() => null);
  return payload?.ok === true;
}

function normalizeList(value) {
  const values = Array.isArray(value) ? value : [value];
  return Array.from(
    new Set(
      values
        .flatMap((item) => String(item || "").split(","))
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function normalizeTopics(value) {
  return normalizeList(value).filter((topic) => allowedTopics.has(topic));
}

function getClientId() {
  if (clientId) return clientId;
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  clientId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return clientId;
}

function safeCall(fn, arg) {
  if (typeof fn !== "function") return;
  Promise.resolve(fn(arg)).catch(() => null);
}

function safeCallOnce(fn, arg) {
  if (typeof fn !== "function") return;
  if (safeCallOnce.pending.has(fn)) return;
  safeCallOnce.pending.add(fn);
  queueMicrotask(() => {
    safeCallOnce.pending.delete(fn);
    safeCall(fn, arg);
  });
}
safeCallOnce.pending = new Set();

function emitLiveDebug(event, details) {
  if (import.meta.env.VITE_DEBUG_REALTIME !== "1") return;
  console.debug("[live-realtime]", event, details);
}
